import {
  Component,
  inject,
  signal,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
} from "@angular/core";
import { RouterModule, Router } from "@angular/router";
import {
  IonApp,
  IonRouterOutlet,
  IonSplitPane,
  IonMenu,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonIcon,
  IonLabel,
  IonAccordion,
  IonAccordionGroup,
  IonImg,
  IonMenuToggle,
  IonNote,
  IonSearchbar,
  IonFooter,
  MenuController,
  ToastController,
  isPlatform,
} from "@ionic/angular/standalone";
import { CommonModule } from "@angular/common";
import { addIcons } from "ionicons";
import {
  addOutline,
  cameraOutline,
  closeOutline,
  createOutline,
  downloadOutline,
  eyeOutline,
  searchOutline,
  trashOutline,
  storefrontOutline,
  cubeOutline,
  calculatorOutline,
  banOutline,
  bulbOutline,
  clipboardOutline,
  cloudUploadOutline,
  cloudDownloadOutline,
  checkmarkCircleOutline,
  duplicateOutline,
  saveOutline,
  caretDownCircleOutline,
  mapOutline,
  navigateOutline,
  timeOutline,
  speedometerOutline,
  checkmarkCircle,
  closeCircle,
  documentTextOutline,
  addCircleOutline,
  informationCircleOutline,
  peopleOutline,
  personOutline,
  briefcaseOutline,
  folderOutline,
  listOutline,
  gitBranchOutline,
  gitMergeOutline,
  cartOutline,
  businessOutline,
  peopleCircleOutline,
  closeCircleOutline,
  copyOutline,
  analyticsOutline,
  starOutline,
  callOutline,
  mailOutline,
  cloudUpload,
  hourglassOutline,
  helpCircleOutline,
  repeatOutline,
  logOutOutline,
  walletOutline,
  cashOutline,
  trendingUpOutline,
  alertCircleOutline,
  pulseOutline,
  swapHorizontalOutline,
  chatbubbleOutline,
  chevronDown,
  syncOutline,
  chevronForward,
  constructOutline,
  imagesOutline
} from "ionicons/icons";
import { register } from "swiper/element/bundle";
import { MenuService } from "./services/menu.service";
import { MenuSection, MenuItem, HybridMenuItem } from "./interfaces/menu.interface";
import { AuthService } from "./services/auth.service";
import { CognitoService } from "./services/cognito.service";
import { App, AppInfo } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { environment } from "../environments/environment";
import { UserRoleService } from "./services/user-role.service";

register();

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrl: "app.component.scss",
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    RouterModule,
    IonApp,
    IonRouterOutlet,
    IonSplitPane,
    IonMenu,
    IonHeader,
    IonToolbar,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonLabel,
    IonAccordion,
    IonAccordionGroup,
    IonImg,
    IonMenuToggle,
    IonNote,
    IonSearchbar,
    IonFooter,
  ],
})
export class AppComponent implements OnInit {
  private menuService = inject(MenuService);
  private menuController = inject(MenuController);
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastController = inject(ToastController);
  private cognitoService = inject(CognitoService);
  private userRoleService = inject(UserRoleService);

  // Use the hybrid menu data directly from the service
  menuData = this.menuService.getHybridUserClientMenuData();
  // menuData = this.menuService.getHybridUserMenuData();
  filteredMenuData = signal<HybridMenuItem[]>(this.menuData());
  searchQuery = signal("");
  appInfo = signal<AppInfo>({} as AppInfo);
  versionInfo = signal<string>("v1.0.0");

  constructor() {
    addIcons({
      searchOutline,
      briefcaseOutline,
      listOutline,
      analyticsOutline,
      closeOutline,
      eyeOutline,
      createOutline,
      trashOutline,
      addOutline,
      storefrontOutline,
      cubeOutline,
      calculatorOutline,
      downloadOutline,
      banOutline,
      bulbOutline,
      clipboardOutline,
      cloudUploadOutline,
      cloudDownloadOutline,
      checkmarkCircleOutline,
      cameraOutline,
      duplicateOutline,
      saveOutline,
      caretDownCircleOutline,
      mapOutline,
      navigateOutline,
      timeOutline,
      speedometerOutline,
      checkmarkCircle,
      closeCircle,
      documentTextOutline,
      addCircleOutline,
      informationCircleOutline,
      peopleOutline,
      personOutline,
      folderOutline,
      gitBranchOutline,
      gitMergeOutline,
      cartOutline,
      businessOutline,
      peopleCircleOutline,
      closeCircleOutline,
      copyOutline,
      starOutline,
      callOutline,
      mailOutline,
      cloudUpload,
      hourglassOutline,
      helpCircleOutline,
      repeatOutline,
      logOutOutline,
      walletOutline,
      cashOutline,
      trendingUpOutline,
      alertCircleOutline,
      pulseOutline,
      swapHorizontalOutline,
      chatbubbleOutline,
      chevronDown,
      syncOutline ,
      chevronForward,
      constructOutline,
      imagesOutline
    });
  }

  async ngOnInit() {
    // Enable the menu and ensure it's visible
    await this.menuController.enable(true, "leftMenu");

    // Load and log the user role
    // Use setTimeout to ensure UserRoleService is fully initialized
    setTimeout(async () => {
      const role = await this.userRoleService.loadUserRole();
      // Role is already converted to lowercase in the UserRoleService
    }, 1500);

    // Initialize menu sections as not expanded and filter mobile-only items
    const initialData = this.menuData().map((item: HybridMenuItem) => {
      if (item.type === 'section') {
        // Filter items based on platform for sections
        const filteredItems = item.items?.filter(subItem => 
          !subItem.mobileOnly || (subItem.mobileOnly && isPlatform('mobile'))
        ) || [];

        return {
          ...item,
          isExpanded: false,
          items: filteredItems
        };
      } else {
        // For single items, just check if they should be shown on this platform
        return !item.mobileOnly || (item.mobileOnly && isPlatform('mobile')) 
          ? item 
          : null;
      }
    }).filter(Boolean); // Remove null items
    
    this.filteredMenuData.set(initialData as HybridMenuItem[]);

    // Get app version info if on native platform
    if (Capacitor.isNativePlatform()) {
      try {
        const info = await App.getInfo();
        this.appInfo.set(info);
        this.versionInfo.set(
          `v${info.version} (${environment.production ? "-live" : "-staging"})`,
        );
      } catch (error) {
        console.error("Error getting app info:", error);
      }
    }
  }

  handleSearch(event: any) {
    const query = event.detail.value.toLowerCase();
    this.searchQuery.set(query);

    if (!query) {
      // Reset to initial state when search is cleared
      const unexpandedData = this.menuData().map((item: HybridMenuItem) => {
        if (item.type === 'section') {
          // Filter items based on platform for sections
          const filteredItems = item.items?.filter(subItem => 
            !subItem.mobileOnly || (subItem.mobileOnly && isPlatform('mobile'))
          ) || [];

          return {
            ...item,
            isExpanded: false,
            items: filteredItems
          };
        } else {
          // For single items, just check if they should be shown on this platform
          return !item.mobileOnly || (item.mobileOnly && isPlatform('mobile')) 
            ? item 
            : null;
        }
      }).filter(Boolean);
      
      this.filteredMenuData.set(unexpandedData as HybridMenuItem[]);
      return;
    }

    // Filter menu data based on search query
    const filtered = this.menuData()
      .map((item: HybridMenuItem) => {
        if (item.type === 'section') {
          // For sections, filter their items
          const matchedItems = item.items?.filter(
            (subItem) =>
              subItem.title.toLowerCase().includes(query) &&
              (!subItem.mobileOnly || (subItem.mobileOnly && isPlatform('mobile')))
          ) || [];

          if (matchedItems.length > 0) {
            return {
              ...item,
              isExpanded: true,
              items: matchedItems,
            };
          }
          return null;
        } else {
          // For single items, check if they match the query
          return item.title.toLowerCase().includes(query) &&
            (!item.mobileOnly || (item.mobileOnly && isPlatform('mobile')))
            ? item
            : null;
        }
      })
      .filter(Boolean);

    this.filteredMenuData.set(filtered as HybridMenuItem[]);
  }

  async handleLogout() {
    try {
      await this.authService.logout();
      await this.cognitoService.unauthenticate();

      const toast = await this.toastController.create({
        message: "Logged out successfully",
        duration: 2000,
        position: "bottom",
        color: "success",
        icon: "checkmark-circle-outline",
      });
      await toast.present();

      // Navigate to login page
      await this.router.navigate(["/login"], { replaceUrl: true });
    } catch (error) {
      const toast = await this.toastController.create({
        message: "Failed to logout. Please try again.",
        duration: 3000,
        position: "bottom",
        color: "danger",
        icon: "close-circle-outline",
      });
      await toast.present();
      console.error("Logout error:", error);
    }
  }
}


